// Channel-stream encoders for PSD/PSB — counterpart to `decompressors.ts`.
//
// Currently implemented:
//   0  raw                 — bytes verbatim (encodeRaw)
//   1  PackBits / RLE      — Apple TN1023 byte-oriented RLE (encodePackBits)
//
// Not implemented (deferred):
//   2  ZIP without prediction      — would require a deterministic deflate
//                                    implementation for round-trip fidelity
//   3  ZIP with prediction         — same as above, plus prediction step
//
// Skipping the ZIP encoders is intentional: PSD readers (including ours,
// Photoshop's, libpsd, ImageMagick) always handle all four compression codes,
// so we can write everything as PackBits and remain fully interoperable.
// Writing ZIP without a vendor-matched deflate also makes byte-exact
// round-trip checks impossible.
//
// PackBits encoder strategy (matches Photoshop's behaviour for round-trip
// stability):
//
//   - Walk the input.  At each step, look ahead and see if the next two
//     bytes form a "useful" run of ≥3 identical bytes.  If yes, flush any
//     accumulated literal and emit a repeat run.  If no, append the current
//     byte to the literal buffer.
//   - Repeat runs are capped at 128 bytes (encoded byte = 1 - count, in
//     [-127, 0], stored as 0x81..0xFF).
//   - Literal runs are capped at 128 bytes (encoded header byte = count - 1
//     in [0, 127]).
//   - The reserved control byte 0x80 (-128, "no-op") is never emitted; it's
//     read-only.
//
// This implementation is verified by canonical round-trip: encode → decode
// recovers the input exactly.  It does not aim for bit-exact equivalence
// with Photoshop's encoder (different versions of Photoshop produce slightly
// different outputs for the same input) — only for size-reasonable,
// decoder-friendly output.

/** Identity encoder — present for symmetry with the 4-way decoder switch. */
export function encodeRaw(src: Uint8Array): Uint8Array {
  // Return a fresh copy so callers can mutate / hand off without surprises.
  return new Uint8Array(src);
}

/**
 * Apple PackBits encoder.  Produces a byte stream that decodes back to
 * `src` via `decodePackBits(out, src.length)`.  Worst-case output size is
 * `src.length + ceil(src.length / 128)`.
 */
export function encodePackBits(src: Uint8Array): Uint8Array {
  if (src.length === 0) return new Uint8Array(0);

  // Pre-size output: upper bound = src.length + ceil(src.length/128) + 1.
  // We'll slice down at the end.
  const upperBound = src.length + Math.ceil(src.length / 128) + 4;
  const out = new Uint8Array(upperBound);
  let oi = 0;

  // Buffered literal bytes — flushed when we hit a run or fill up to 128.
  const MAX = 128;
  let literalStart = 0; // index into src of first un-flushed literal byte
  let literalLen = 0;

  const flushLiteral = (): void => {
    if (literalLen === 0) return;
    // Emit chunks of up to 128.
    let lo = literalStart;
    let remaining = literalLen;
    while (remaining > 0) {
      const chunk = Math.min(MAX, remaining);
      out[oi++] = (chunk - 1) & 0xff; // header: count-1 in [0,127]
      for (let k = 0; k < chunk; k++) {
        out[oi++] = src[lo + k]!;
      }
      lo += chunk;
      remaining -= chunk;
    }
    literalLen = 0;
  };

  let i = 0;
  while (i < src.length) {
    // Look for a run of identical bytes starting at i.
    let runLen = 1;
    const v = src[i]!;
    while (i + runLen < src.length && src[i + runLen] === v && runLen < MAX) {
      runLen += 1;
    }

    if (runLen >= 3) {
      // Worth emitting as a repeat run — flush any pending literal first.
      flushLiteral();
      // Header: 1 - count, in [-127, -2], stored as a signed byte (0x81..0xFE).
      const header = 1 - runLen; // negative
      out[oi++] = header & 0xff;
      out[oi++] = v;
      i += runLen;
      literalStart = i;
    } else {
      // Add this byte to the literal buffer; flush when it hits the cap.
      literalLen += 1;
      i += 1;
      if (literalLen === MAX) {
        flushLiteral();
        literalStart = i;
      }
    }
  }
  flushLiteral();

  return out.slice(0, oi);
}
